import React, { useState } from 'react';
import { BookOpen, ChevronDown } from 'lucide-react';
import { GMP_FAQS } from '../../data/gmpBoardData';

const DEFAULT_EXAMPLE =
  'Example: if an issue is priced at ₹127 and its GMP is ₹127, the market expects listing near ₹254 per share (+100%).';

export const GmpKnowledgeDesk = ({
  title = 'Grey Market Premium (GMP) & Kostak Knowledge Desk',
  subtitle = 'Essential guidance for retail investors on informal pricing before placing IPO bids.',
  example = DEFAULT_EXAMPLE,
  faqs = GMP_FAQS,
}) => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-6" aria-labelledby="gmp-knowledge-heading">
      <h2 id="gmp-knowledge-heading" className="flex items-center gap-2 text-base sm:text-lg font-extrabold text-slate-900 tracking-tight">
        <BookOpen className="w-5 h-5 text-[#008080]" />
        {title}
      </h2>
      <p className="text-xs sm:text-sm text-slate-500 mt-1">{subtitle}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Estimated Listing Formula</h3>
          <div className="mt-3 rounded-lg bg-white border border-slate-200 px-3 py-2.5 font-mono text-xs sm:text-[13px] font-semibold text-[#006666] leading-relaxed">
            Estimated Listing Price = Issue Price [Cut-off] + GMP
          </div>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">{example}</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Subject to Sauda (SS) vs Kostak</h3>
          <dl className="mt-3 space-y-2.5 text-xs text-slate-600 leading-relaxed">
            <div>
              <dt className="inline font-bold text-slate-800">Kostak Rate: </dt>
              <dd className="inline">
                A fixed amount paid for an entire IPO application before allotment, whether or not shares are
                allotted.
              </dd>
            </div>
            <div>
              <dt className="inline font-bold text-slate-800">Subject to Sauda: </dt>
              <dd className="inline">
                A fixed amount for an application that is paid only if the application actually receives an
                allotment.
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* FAQ Accordion */}
      <div className="mt-5 space-y-2.5">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          const panelId = `gmp-faq-panel-${index}`;
          return (
            <div key={faq.question} className="rounded-xl border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm font-bold transition-colors ${
                  isOpen ? 'bg-teal-50/60 text-[#006666]' : 'text-slate-800 hover:bg-slate-50'
                }`}
              >
                <span>
                  {index + 1}. {faq.question}
                </span>
                <ChevronDown
                  className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isOpen && (
                <ul id={panelId} className="px-4 sm:px-5 py-3.5 space-y-2 border-t border-slate-200 list-disc list-inside marker:text-[#008080]">
                  {faq.points.map((point) => (
                    <li key={point} className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                      {point}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default GmpKnowledgeDesk;
